import { useFormik } from 'formik';
import { object, string } from 'yup';
import { useToast } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { useCreateContextIdResource } from '../api/useCreateContextIdResource';
import { useGetAvailableProducts } from '../api/useGetAvailableProducts';
import { env as envConfig } from 'features/common/config/envConfig';
import { useGetTenantsQuery } from 'gql/graphql';
import { USER_SEARCH_RESOURCE_ID } from './embeddedResources';
import { SEARCH_RESOURCE_NAME, SEARCH_RESOURCE_TYPE } from '../constants';
import {
  buildSearchTenantCandidates,
  getAvailableProductsArray,
} from '../utils';

const initialValues = {
  domainId: '',
  region: '',
  apiKey: '',
  tenantId: '',
};

export type AddSearchResourceValues = typeof initialValues;

const normalizeCode = (value?: string) =>
  (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

const isSearchTenantProductCode = (tenantProductCode?: string) =>
  normalizeCode(tenantProductCode).includes('search');

export const useAddSearchResource = ({
  sitecoreContextId,
  onClose,
  excludedTenantIds = [],
}: {
  sitecoreContextId: string;
  onClose: () => void;
  excludedTenantIds?: string[];
}) => {
  const toast = useToast();
  const { mutate: createResource, isLoading } = useCreateContextIdResource({
    sitecoreContextId,
  });

  const {
    data: availableProductsResponse,
    isLoading: isLoadingTenants,
    isError: isTenantsError,
  } = useGetAvailableProducts();
  const { data: tenantsData } = useGetTenantsQuery({
    variables: {
      first: 100,
      env: envConfig,
    },
  });

  const availableProducts = useMemo(
    () => getAvailableProductsArray(availableProductsResponse?.data),
    [availableProductsResponse?.data],
  );

  const searchTenantCandidates = useMemo(() => {
    const allTenantCandidates = buildSearchTenantCandidates({
      availableProducts,
      tenantNodes: tenantsData?.user?.applications?.nodes,
    });

    // Dropdown must only include true Search tenants (exclude Discover).
    const excludedTenantIdsSet = new Set(excludedTenantIds);
    return allTenantCandidates.filter(
      (tenantCandidate) =>
        isSearchTenantProductCode(tenantCandidate.productCode) &&
        !excludedTenantIdsSet.has(tenantCandidate.value),
    );
  }, [
    availableProducts,
    tenantsData?.user?.applications?.nodes,
    excludedTenantIds,
  ]);

  const searchTenantOptions = useMemo(
    () =>
      searchTenantCandidates.map(({ value, label }) => ({
        value,
        label,
      })),
    [searchTenantCandidates],
  );

  const validationSchema = object({
    domainId: string()
      .trim()
      .min(3, 'Domain ID must be at least 3 characters')
      .max(255, 'Domain ID must be at most 255 characters')
      .matches(
        /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/,
        'Domain ID can only include letters, numbers, dots, underscores, and dashes',
      )
      .required('Domain ID is required'),
    region: string()
      .trim()
      .min(2, 'Region must be at least 2 characters')
      .max(100, 'Region must be at most 100 characters')
      .matches(
        /^[a-zA-Z0-9-]+$/,
        'Region can only include letters, numbers, and dashes',
      )
      .required('Region is required'),
    apiKey: string()
      .trim()
      .min(8, 'API key must be at least 8 characters')
      .max(512, 'API key must be at most 512 characters')
      .matches(/^\S+$/, 'API key cannot contain spaces')
      .required('API key is required'),
    tenantId: string().required('Search environment is required'),
  });

  const {
    errors,
    touched,
    values,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    resetForm,
    isValid,
  } = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (formValues) => {
      const payload = {
        id: USER_SEARCH_RESOURCE_ID,
        name: SEARCH_RESOURCE_NAME,
        type: SEARCH_RESOURCE_TYPE,
        tenantId: formValues.tenantId,
        properties: [
          { id: 'domain', value: formValues.domainId },
          { id: 'apiKey', value: formValues.apiKey },
          { id: 'region', value: formValues.region },
        ],
      };

      createResource(payload, {
        onSuccess: () => {
          toast({
            status: 'success',
            description: 'Search resource added successfully.',
          });
          resetForm();
          onClose();
        },
        onError: (error) => {
          const errorDetail = (error as AxiosError<{ detail?: string }>)
            ?.response?.data?.detail;
          toast({
            status: 'error',
            description: errorDetail ?? 'Failed to add search resource.',
          });
        },
      });
    },
  });

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return {
    errors,
    touched,
    values,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    handleClose,
    isLoading,
    isLoadingTenants,
    isTenantsError,
    searchTenantOptions,
    isSubmitDisabled:
      !values.domainId.trim() ||
      !values.region.trim() ||
      !values.apiKey.trim() ||
      !values.tenantId ||
      !isValid ||
      isLoading ||
      searchTenantOptions.length === 0,
  };
};
